/**
 * Patient Search Hook
 * Feature: 004-restore-missing-features
 * Task: T007
 *
 * Provides patient search functionality for registration modal
 * Allows searching across all patients and linking to existing ones
 */

import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type { PatientSearchResult } from '@/features/shared/types/patient.types'

/**
 * Calculate age from birth date
 */
function calculateAge(birthDate: string): number {
  const birth = new Date(birthDate)
  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const monthDiff = today.getMonth() - birth.getMonth()

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--
  }

  return age
}

/**
 * Debounce hook for search input
 */
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useState(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  })

  // Use effect for actual debouncing
  useMemo(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

interface UsePatientSearchOptions {
  /** Minimum characters before search triggers */
  minSearchLength?: number
  /** Debounce delay in ms */
  debounceMs?: number
}

/**
 * Hook for searching patients across the system
 * Used in patient registration modal to find and link existing patients
 */
export function usePatientSearch(options: UsePatientSearchOptions = {}) {
  const { minSearchLength = 2, debounceMs = 300 } = options
  const { currentOrganization, userProfile } = useAuth()
  const queryClient = useQueryClient()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, debounceMs)

  // Query to search patients
  const searchQuery = useQuery({
    queryKey: ['patient-search', debouncedSearch],
    queryFn: async (): Promise<PatientSearchResult[]> => {
      if (!debouncedSearch || debouncedSearch.length < minSearchLength) {
        return []
      }

      // Search patients by name across all organizations
      const { data: patients, error } = await supabase
        .from('patients')
        .select(`
          id,
          full_name,
          birth_date,
          condition_description,
          organization_id,
          patient_care_team (
            id,
            professional_id,
            is_active,
            profiles!patient_care_team_professional_id_fkey (
              id,
              full_name
            )
          )
        `)
        .ilike('full_name', `%${debouncedSearch}%`)
        .eq('status', 'active')
        .limit(20)

      if (error) throw error

      // Transform data to PatientSearchResult format
      return (patients || []).map((patient) => {
        const assignments = patient.patient_care_team || []
        const professionals = assignments
          .filter((a: { is_active: boolean }) => a.is_active)
          .map((a: { profiles: { id: string; full_name: string }[] | { id: string; full_name: string } | null }) => {
            // Supabase can return profiles as array or object depending on the join
            const profile = Array.isArray(a.profiles) ? a.profiles[0] : a.profiles
            return {
              id: profile?.id || '',
              name: profile?.full_name || 'Profissional',
            }
          })

        // Check if current user is already assigned to this patient
        const isAlreadyAssigned = assignments.some(
          (a: { professional_id: string; is_active: boolean }) =>
            a.professional_id === userProfile?.id && a.is_active
        )

        return {
          id: patient.id,
          full_name: patient.full_name,
          birth_date: patient.birth_date,
          condition_description: patient.condition_description,
          age: calculateAge(patient.birth_date),
          professionals,
          isAlreadyAssigned,
        }
      })
    },
    enabled: debouncedSearch.length >= minSearchLength,
    staleTime: 30000, // 30 seconds
  })

  // Mutation to link current professional to a patient
  const linkMutation = useMutation({
    mutationFn: async (patientId: string): Promise<void> => {
      if (!userProfile?.id || !currentOrganization?.id) {
        throw new Error('Usuario ou organizacao nao encontrados')
      }

      // Check if assignment already exists
      const { data: existing } = await supabase
        .from('patient_care_team')
        .select('id, is_active')
        .eq('patient_id', patientId)
        .eq('professional_id', userProfile.id)
        .maybeSingle()

      if (existing) {
        if (existing.is_active) {
          throw new Error('Voce ja esta vinculado a este paciente')
        }

        // Reactivate existing assignment
        const { error } = await supabase
          .from('patient_care_team')
          .update({ is_active: true })
          .eq('id', existing.id)

        if (error) throw error
      } else {
        // Create new assignment
        const { error } = await supabase.from('patient_care_team').insert({
          organization_id: currentOrganization.id,
          patient_id: patientId,
          professional_id: userProfile.id,
          is_active: true,
        })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['patient-search'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
    },
  })

  // Search function
  const search = useCallback(
    async (term: string): Promise<PatientSearchResult[]> => {
      setSearchTerm(term)
      // Wait for debounced query to complete
      if (term.length >= minSearchLength) {
        await searchQuery.refetch()
      }
      return searchQuery.data || []
    },
    [minSearchLength, searchQuery]
  )

  // Link to patient function
  const linkToPatient = useCallback(
    async (patientId: string): Promise<void> => {
      await linkMutation.mutateAsync(patientId)
    },
    [linkMutation]
  )

  // Computed values
  const results = useMemo(() => {
    if (debouncedSearch.length < minSearchLength) return []
    return searchQuery.data || []
  }, [searchQuery.data, debouncedSearch, minSearchLength])

  return {
    // State
    searchTerm,
    setSearchTerm,
    results,

    // Status
    isSearching: searchQuery.isFetching,
    isLinking: linkMutation.isPending,
    searchError: searchQuery.error,
    linkError: linkMutation.error,

    // Actions
    search,
    linkToPatient,

    // Clear search - memoized to prevent infinite loops in useEffect dependencies
    clearSearch: useCallback(() => {
      setSearchTerm('')
    }, []),
  }
}
